import React from 'react';
import { Separator } from '@frontend/shadcn/components/ui/separator';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import Text from '@frontend/app/refresh-components/Text';
import { formatValue, DataFormat } from '../../utils';
import { getLocaleString } from '../../utils/dateTimeUtils';
import { TierAggregationDurationTypes } from '../../types/globalTypes';

interface PayoutLineItem {
  id: string;
  name: string;
  payout: number;
  __typename?: string;
}

interface PayoutRuleData {
  ruleTitle: string;
  totalPayout: number;
  lineItems: PayoutLineItem[] | null;
  orderPlacedAt: Date;
  orderSubtotal: number;
  currency?: string;
  flatPayout?: number;
  percentPayout?: number;
  isAutomatedPayout?: boolean;
  automatedPayoutLog?: {
    automationExecutionId?: string;
    automationStatus: string;
    aggregationType: TierAggregationDurationTypes;
    totalSales: number;
    totalConversions: number;
    payoutIdFromAutomation: number;
    payoutName: string;
    flatPayout?: number;
    percentPayout?: number;
  }
  __typename?: string;
}

interface PayoutLogsResponse {
  getOfferPayoutLogs: PayoutRuleData[];
}

export enum RuleConditionTypes {
  ORDER_SUBTOTAL = 'ORDER_SUBTOTAL',
  COLLECTIONS = 'COLLECTIONS',
  PRODUCTS = 'PRODUCTS',
  DURATION = 'DURATION',
  RECURRING = 'RECURRING',
  DEFAULT = 'DEFAULT',
}

function shouldConvertCurrency(ruleTitle: string): boolean {
  return [RuleConditionTypes.PRODUCTS, RuleConditionTypes.COLLECTIONS, RuleConditionTypes.RECURRING].includes(ruleTitle as RuleConditionTypes);
}

interface PayoutFormat {
  label: string;
  flatPayout?: number;
  percentPayout?: number;
}

function formatPayoutString({ label, flatPayout, percentPayout }: PayoutFormat): string {
  if (flatPayout && percentPayout) {
    return `${label} (${formatValue(DataFormat.Money, flatPayout)} + ${percentPayout}%)`;
  }
  if (flatPayout) {
    return `${label} (${formatValue(DataFormat.Money, flatPayout)})`;
  }
  if (percentPayout) {
    return `${label} (${percentPayout}%)`;
  }
  return label;
}

function getPayoutLabel(payoutLabel: string, automatedPayoutLog: PayoutRuleData['automatedPayoutLog']): string {
  if (!automatedPayoutLog) {
    return payoutLabel;
  }

  return formatPayoutString({
    label: automatedPayoutLog.payoutName,
    flatPayout: automatedPayoutLog.flatPayout,
    percentPayout: automatedPayoutLog.percentPayout,
  });
}

export function PayoutCalculationDialog({
  data,
  payoutLabel,
  currency,
  exchangeRate,
}: {
  data: PayoutLogsResponse,
  payoutLabel?: string,
  currency?: string,
  exchangeRate?: number
}): JSX.Element {
  const currentCurrency = currency || 'USD';
  let payoutLogs = data?.getOfferPayoutLogs || [];

  function convertToUSD(amount: number): number {
    if (currentCurrency === 'USD' || !exchangeRate) return amount;
    return amount * (exchangeRate ?? 1);
  }

  const automatedPayoutInfo = payoutLogs.filter((log) => log.isAutomatedPayout);
  payoutLogs = payoutLogs.filter((log) => !log.isAutomatedPayout);

  const automatedPayoutLog = automatedPayoutInfo.length > 0 ? automatedPayoutInfo[0].automatedPayoutLog : null;
  const automatedPayoutLogTotalSales = automatedPayoutLog?.totalSales || 0;
  const automatedPayoutLogTotalConversions = automatedPayoutLog?.totalConversions || 0;

  const updatedPayoutLabel = getPayoutLabel(payoutLabel, automatedPayoutLog);

  // Calculate total payout in USD
  let totalPayout = payoutLogs.reduce((sum, log) => {
    let earned = log.totalPayout;
    if (shouldConvertCurrency(log.ruleTitle) && currentCurrency !== 'USD') {
      earned = convertToUSD(log.totalPayout);
    }
    return sum + (earned || 0);
  }, 0);

  // if no rules are applied, then the total payout will be 0, so we have to show the payout from automated payout log
  if (automatedPayoutLog && !totalPayout) {
    totalPayout = convertToUSD(automatedPayoutInfo[0].totalPayout);
  }

  return (
    <Card className="w-[362px] border-none bg-white">
      <CardContent className="flex flex-col gap-2 p-0">
        <Text visualVariant="paragraph" className="text-grey-6 font-medium">
          How payout is calculated?
        </Text>

        <div className="flex flex-col gap-2">
          <div className="w-full bg-grey-2 rounded-lg py-1 px-2">
            <Text visualVariant="caption" className="text-grey-6">
              Payout Applied: {updatedPayoutLabel || 'N/A'}
            </Text>
            {automatedPayoutLog && (
              <div className="flex flex-col gap-1">
                <Text visualVariant="caption" className="text-grey-6 font-medium">
                  Total Sales: ${formatValue(DataFormat.Money, automatedPayoutLogTotalSales)}
                </Text>
                <Text visualVariant="caption" className="text-grey-6 font-medium">
                  Total Conversions: {automatedPayoutLogTotalConversions}
                </Text>
              </div>
            )}
          </div>

          <Separator className="bg-grey-2" />

          <div className="flex flex-col gap-2">
            {/* Headers */}
            {payoutLogs.length > 0 && (
              <div className="grid grid-cols-12 gap-2">
                <Text visualVariant="caption" className="text-grey-6 font-medium col-span-4">Applied On</Text>
                <Text visualVariant="caption" className="text-grey-6 font-medium col-span-3">Amount</Text>
                <Text visualVariant="caption" className="text-grey-6 font-medium col-span-2">Payout</Text>
                <Text visualVariant="caption" className="text-grey-6 font-medium col-span-3">Earned</Text>
              </div>)}

            {/* Row Data */}
            {payoutLogs.map((log, index) => {
              let displayAmount = log.orderSubtotal;
              let displayPayout = log.totalPayout;
              if (shouldConvertCurrency(log.ruleTitle) && currentCurrency !== 'USD') {
                displayAmount = convertToUSD(log.orderSubtotal);
                displayPayout = convertToUSD(log.totalPayout);
              }
              return (
                <div key={index} className="grid grid-cols-12 gap-2">
                  <Text visualVariant="caption" className="text-grey-5 col-span-4">
                    {log.ruleTitle}
                    {log.ruleTitle === RuleConditionTypes.DURATION ? (
                      <span className="text-grey-5 px-1">
                        (Order Placed At - {getLocaleString(log.orderPlacedAt)})
                      </span>
                    ) : (
                      log.lineItems?.map((item) => (
                        <div key={item.id}>
                          {item.name.split(',').map((name) => (
                            <div key={name}>
                              ({name})
                            </div>
                          ))}
                        </div>
                      ))
                    )}
                  </Text>
                  <Text visualVariant="caption" className="text-grey-5 col-span-3">
                    ${formatValue(DataFormat.Money, displayAmount)}
                  </Text>
                  <Text visualVariant="caption" className="text-grey-5 col-span-2">
                    {log.flatPayout > 0 ? `$${formatValue(DataFormat.Money, log.flatPayout)}` :
                     log.percentPayout > 0 ? `${log.percentPayout}%` : '0'}
                  </Text>
                  <Text visualVariant="caption" className="text-grey-5 col-span-3">
                    ${formatValue(DataFormat.Money, displayPayout)}
                  </Text>
                </div>
              );
            })}
          </div>

          <Separator className="bg-grey-2" />

          <div className="flex justify-between items-center">
            <Text visualVariant="paragraph" className="text-grey-6 font-medium">
              Payout Earned
            </Text>
            <Text visualVariant="paragraph" className="text-grey-6 font-medium px-4">
              ${formatValue(DataFormat.Money, totalPayout)}
            </Text>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
